import { Menu } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { HeaderOverlayProvider, useHeaderOverlay } from '../lib/header-overlay'
import { Button } from './button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './sheet'

export interface AppShellProps {
  /** Product / competition name shown at the far left of the header. */
  brand: ReactNode
  /** Right-hand header slot at `lg` and up: nav links, switchers, actions. */
  headerEnd?: ReactNode
  /** Always-visible compact controls next to the mobile menu trigger (e.g. language switcher). */
  mobileActions?: ReactNode
  /**
   * Content of the mobile drawer opened by the hamburger trigger. Omit to
   * hide the trigger entirely (falls back to `headerEnd` wrapping, as before).
   */
  mobileMenu?: ReactNode
  /** Accessible title for the mobile drawer's hidden heading. */
  mobileMenuTitle?: string
  /** aria-label for the hamburger trigger button. */
  mobileMenuLabel?: string
  /** sr-only label for the drawer's close button. */
  mobileMenuCloseLabel?: string
  /** Skip-navigation link label (UX-064) — hidden until keyboard focus. */
  skipToContentLabel?: string
  footer?: ReactNode
  children: ReactNode
  className?: string
}

/**
 * The application frame. Navigation content itself is assembled by the
 * caller (it depends on session/role state) — this only owns the responsive
 * shell: a horizontal `headerEnd` row at `lg` and up, collapsing to a
 * hamburger-triggered drawer (`mobileMenu`) below it. The breakpoint sits at
 * `lg` rather than `sm` because a signed-in header can carry a nav link, a
 * competition switcher, the language switcher, and a sign-out button all at
 * once — that content overlaps the brand well before 640px, most visibly
 * around common tablet widths (~768–900px).
 */
export function AppShell(props: AppShellProps) {
  return (
    <HeaderOverlayProvider>
      <AppShellLayout {...props} />
    </HeaderOverlayProvider>
  )
}

function AppShellLayout({
  brand,
  headerEnd,
  mobileActions,
  mobileMenu,
  mobileMenuTitle,
  mobileMenuLabel,
  mobileMenuCloseLabel,
  skipToContentLabel,
  footer,
  children,
  className,
}: AppShellProps) {
  // UX-092/BR-110 (v3.26): the drawer and the caller-owned Account popover
  // must never both be visible — routed through the same `overlay` switch
  // the Account trigger's `DropdownMenu` also reads, rather than each
  // surface tracking its own independent `open` boolean.
  const { overlay, setOverlay } = useHeaderOverlay()

  return (
    <div className={cn('flex min-h-dvh flex-col bg-background text-foreground', className)}>
      {skipToContentLabel ? (
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
        >
          {skipToContentLabel}
        </a>
      ) : null}
      {/* PR-140/UX-087 (v3.26): normal flow flex, not stacked absolute
          layers — brand and nav both get `min-width: 0` so they can shrink
          instead of overlapping, and the actions cluster never shrinks. */}
      <header className="sticky top-0 z-40 border-b border-border bg-background print:hidden">
        <div className="mx-auto flex h-14 w-full max-w-[1400px] items-center gap-3 px-4 sm:px-6">
          <div className="flex min-w-0 shrink items-center gap-2 whitespace-nowrap font-semibold">
            {brand}
          </div>

          {headerEnd ? (
            <div className="hidden min-w-0 flex-1 items-center justify-end gap-3 lg:flex">
              {headerEnd}
            </div>
          ) : null}

          {mobileActions || mobileMenu ? (
            <div
              data-testid="mobile-header-actions"
              className="ml-auto flex shrink-0 items-center gap-2 lg:hidden"
            >
              {mobileActions}
              {mobileMenu ? (
                <Sheet
                  open={overlay === 'navigation'}
                  onOpenChange={(open) => setOverlay(open ? 'navigation' : null)}
                >
                  <SheetTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={mobileMenuLabel ?? 'Menu'}
                      className="size-11 shrink-0"
                    >
                      <Menu className="size-5" aria-hidden="true" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent
                    side="right"
                    className="w-[min(84vw,360px)] max-w-[360px] h-dvh"
                    closeLabel={mobileMenuCloseLabel}
                  >
                    <SheetHeader>
                      <SheetTitle>{mobileMenuTitle ?? 'Menu'}</SheetTitle>
                    </SheetHeader>
                    <div className="flex flex-col gap-4 overflow-y-auto px-4 pb-4">
                      {mobileMenu}
                    </div>
                  </SheetContent>
                </Sheet>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      <main
        id="main-content"
        className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-8 sm:px-6 sm:py-10 print:p-0"
      >
        {children}
      </main>

      {footer ? (
        <footer className="border-t border-border print:hidden">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-6 text-sm text-muted-foreground sm:px-6">
            {footer}
          </div>
        </footer>
      ) : null}
    </div>
  )
}
